import { Icon } from "@captions-web/icons";
import { sleep } from "@captions-web/utils/sleep";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";

import { Button } from "~/components/primitives/button";
import { CircularProgress } from "~/components/primitives/circular-progress";
import { Dialog } from "~/components/primitives/dialog";
import { DropdownMenu } from "~/components/primitives/dropdown-menu";
import { textInputStyles } from "~/components/primitives/input";
import { Popover } from "~/components/primitives/popover";
import { ScrollArea } from "~/components/primitives/scroll-area";
import { SegmentedControl } from "~/components/primitives/segmented-control";
import { Select } from "~/components/primitives/select";
import { Spinner } from "~/components/primitives/spinner";
import { Switch } from "~/components/primitives/switch";
import { textTruncateStyles } from "~/components/primitives/text-truncate";
import { Tooltip } from "~/components/primitives/tooltip";
import { TeamNameLogoDialog } from "~/modules/onboarding/components/team-name-logo-dialog";
import { css } from "~/styled-system/css";

export const Route = createFileRoute("/storybook/primitives")({
  component: RouteComponent,
});

function RouteComponent() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTeamNameLogoDialogOpen, setIsTeamNameLogoDialogOpen] = useState(false);
  const [selectValue, setSelectValue] = useState<string>("option1");

  return (
    <div className={rootStyles}>
      <div className={sectionStyles}>
        <h2 className={titleStyles}>Icons</h2>
        <div className={rowStyles}>
          <Icon name="copy" size={16} />
          <Icon name="copy" size={20} />
          <Icon name="copy" size={24} />
          <Icon name="copy" size={32} />
          <Icon name="copy" size={48} />
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Buttons</h2>
        <div className={rowStyles}>
          <p className={labelStyles}>xs</p>
          <Button variant="primary" size="xs">
            Primary
          </Button>
          <Button variant="primary" size="xs" rounded="full">
            Primary
          </Button>
          <Button variant="secondary" size="xs">
            Secondary
          </Button>
          <Button variant="tertiary" size="xs">
            Tertiary
          </Button>
          <Button variant="quarternary" size="xs">
            Quarternary
          </Button>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>sm</p>
          <Button variant="primary" size="sm">
            Primary
          </Button>
          <Button variant="primary" size="sm" rounded="full">
            Primary
          </Button>
          <Button variant="secondary" size="sm">
            Secondary
          </Button>
          <Button variant="tertiary" size="sm">
            Tertiary
          </Button>
          <Button variant="quarternary" size="sm">
            Quarternary
          </Button>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>md</p>
          <Button variant="primary" size="md">
            Primary
          </Button>
          <Button variant="primary" size="md" rounded="full">
            Primary
          </Button>
          <Button variant="secondary" size="md">
            Secondary
          </Button>
          <Button variant="tertiary" size="md">
            Tertiary
          </Button>
          <Button variant="quarternary" size="md">
            Quarternary
          </Button>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>lg</p>
          <Button variant="primary" size="lg">
            Primary
          </Button>
          <Button variant="primary" size="lg" rounded="full">
            Primary
          </Button>
          <Button variant="secondary" size="lg">
            Secondary
          </Button>
          <Button variant="tertiary" size="lg">
            Tertiary
          </Button>
          <Button variant="quarternary" size="lg">
            Quarternary
          </Button>
        </div>
        <div className={subSectionStyles}>
          <h3 className={subtitleStyles}>Additional Variants</h3>
          <div className={rowStyles}>
            <p className={labelStyles}>disabled</p>
            <Button variant="primary" size="sm" disabled>
              Primary
            </Button>
            <Button variant="secondary" size="sm" disabled>
              Secondary
            </Button>
            <Button variant="tertiary" size="sm" disabled>
              Tertiary
            </Button>
            <Button variant="quarternary" size="sm" disabled>
              Quarternary
            </Button>
          </div>
          <div className={rowStyles}>
            <p className={labelStyles}>isPending</p>
            <Button variant="primary" size="sm" isPending>
              Primary
            </Button>
            <Button variant="secondary" size="sm" isPending>
              Secondary
            </Button>
            <Button variant="tertiary" size="sm" isPending>
              Tertiary
            </Button>
            <Button variant="quarternary" size="sm" isPending>
              Quarternary
            </Button>
          </div>
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Segmented Control</h2>
        <div className={rowStyles}>
          <p className={labelStyles}>default</p>
          <SegmentedControl
            defaultValue="option1"
            items={[
              { value: "option1", label: "Option 1" },
              { value: "option2", label: "Option 2" },
              { value: "option3", label: "Option 3" },
            ]}
          />
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Dropdown Menu</h2>
        <div className={rowStyles}>
          <p className={labelStyles}>basic</p>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button variant="secondary" size="sm">
                Open Menu
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content sideOffset={5}>
              <DropdownMenu.Item icon={<Icon name="edit" />}>Edit Profile</DropdownMenu.Item>
              <DropdownMenu.Item>Account Settings</DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item destructive={true}>Delete Account</DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Select</h2>
        <div className={rowStyles}>
          <p className={labelStyles}>basic</p>
          <Select.Root value={selectValue} onValueChange={setSelectValue}>
            <Select.Trigger className={css({ width: "200px" })} />
            <Select.Content>
              <Select.Item value="option1">Option One</Select.Item>
              <Select.Item value="option2">Option Two</Select.Item>
              <Select.Item value="option3">Option Three</Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>with icons</p>
          <Select.Root defaultValue="apple">
            <Select.Trigger className={css({ width: "200px" })} />
            <Select.Content>
              <Select.Item value="apple" icon={<Icon name="sparkles" />}>
                Apple
              </Select.Item>
              <Select.Item value="banana" icon={<Icon name="copy" />}>
                Banana
              </Select.Item>
              <Select.Item value="orange" icon={<Icon name="edit" />}>
                Orange
              </Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>destructive</p>
          <Select.Root defaultValue="delete">
            <Select.Trigger className={css({ width: "200px" })} />
            <Select.Content>
              <Select.Item value="edit">Edit Item</Select.Item>
              <Select.Item value="duplicate">Duplicate Item</Select.Item>
              <Select.Item value="delete" destructive>
                Delete Item
              </Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>placeholder</p>
          <Select.Root>
            <Select.Trigger placeholder="Choose an option..." className={css({ width: "200px" })} />
            <Select.Content>
              <Select.Item value="small">Small Size</Select.Item>
              <Select.Item value="medium">Medium Size</Select.Item>
              <Select.Item value="large">Large Size</Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>disabled</p>
          <Select.Root disabled>
            <Select.Trigger className={css({ width: "200px" })} />
            <Select.Content>
              <Select.Item value="option1">Option One</Select.Item>
              <Select.Item value="option2">Option Two</Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Dialog</h2>
        <div className={rowStyles}>
          <p className={labelStyles}>basic</p>
          <Dialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <Dialog.Trigger asChild>
              <Button variant="secondary" size="sm">
                Open Dialog
              </Button>
            </Dialog.Trigger>
            <Dialog.SimpleContent
              title="Dialog Title"
              cta={
                <div className={css({ display: "flex", gap: "8px" })}>
                  <Button variant="tertiary" size="sm" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setIsDialogOpen(false)}>
                    Confirm
                  </Button>
                </div>
              }
            >
              <p className={css({ textStyle: "body-2-subtle" })}>
                This is a basic dialog component. It can be used to display important information or
                actions. Dialog content goes here. You can add any content you need.
              </p>
            </Dialog.SimpleContent>
          </Dialog.Root>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>team-name-logo</p>
          <Button variant="secondary" size="sm" onClick={() => setIsTeamNameLogoDialogOpen(true)}>
            Open Team Onboarding
          </Button>
          <TeamNameLogoDialog
            open={isTeamNameLogoDialogOpen}
            onOpenChange={setIsTeamNameLogoDialogOpen}
            initialTeamName="Mirage Team"
            onGetStarted={(teamName, file) => {
              console.log("Get started clicked", { teamName, file });
            }}
            onAddTeamMembers={() => {
              console.log("Add team members clicked");
            }}
          />
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Popover</h2>
        <div className={rowStyles}>
          <p className={labelStyles}>basic</p>
          <Popover.Root>
            <Popover.Trigger asChild>
              <Button variant="secondary" size="sm">
                Open Popover
              </Button>
            </Popover.Trigger>
            <Popover.Content side="right" sideOffset={10} className={css({ height: "500px" })}>
              <p className={css({ textStyle: "body-3-caps", color: "$gray.500" })}>
                Popover Content
              </p>
            </Popover.Content>
          </Popover.Root>
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Tooltip</h2>
        <div className={rowStyles}>
          <p className={labelStyles}>basic</p>

          <Button
            tooltip={{ content: "Basic tooltip content" }}
            variant="secondary"
            size="sm"
            iconPosition="only"
            icon={<Icon name="info" />}
          />
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>sides</p>
          <Tooltip content="Top tooltip" side="top">
            <Button variant="secondary" size="sm">
              Top
            </Button>
          </Tooltip>
          <Tooltip content="Right tooltip" side="right">
            <Button variant="secondary" size="sm">
              Right
            </Button>
          </Tooltip>
          <Tooltip content="Bottom tooltip" side="bottom">
            <Button variant="secondary" size="sm">
              Bottom
            </Button>
          </Tooltip>
          <Tooltip content="Left tooltip" side="left">
            <Button variant="secondary" size="sm">
              Left
            </Button>
          </Tooltip>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>alignment</p>
          <Tooltip content="Start aligned" side="top" align="start">
            <Button variant="secondary" size="sm">
              Start
            </Button>
          </Tooltip>
          <Tooltip content="Center aligned" side="top" align="center">
            <Button variant="secondary" size="sm">
              Center
            </Button>
          </Tooltip>
          <Tooltip content="End aligned" side="top" align="end">
            <Button variant="secondary" size="sm">
              End
            </Button>
          </Tooltip>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>content</p>
          <Tooltip content="This is a longer tooltip with more descriptive content that wraps to multiple lines">
            <Button variant="secondary" size="sm">
              Long content
            </Button>
          </Tooltip>
          <Tooltip
            content={
              <div className={css({ display: "flex", alignItems: "center", gap: "4px" })}>
                <Icon name="sparkles" size={16} /> With icon
              </div>
            }
          >
            <Button variant="secondary" size="sm">
              Rich content
            </Button>
          </Tooltip>
          <Tooltip content="Instant tooltip" delayDuration={0}>
            <Button variant="secondary" size="sm">
              No delay
            </Button>
          </Tooltip>
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>origins</p>
          <Tooltip content="Large trigger with start align" side="top" align="start">
            <Button variant="secondary" size="lg">
              Large button with start align
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Text Styles</h2>
        <div className={textSectionStyles}>
          <div className={textGroupStyles}>
            <p className={css({ textStyle: "heading-1" })}>Heading 1</p>
            <p className={css({ textStyle: "heading-2" })}>Heading 2</p>
            <p className={css({ textStyle: "heading-3" })}>Heading 3</p>
            <p className={css({ textStyle: "heading-4" })}>Heading 4</p>
            <p className={css({ textStyle: "heading-5" })}>Heading 5</p>
          </div>

          <div className={textGroupStyles}>
            <p className={css({ textStyle: "body-1-intense" })}>Body 1 Intense</p>
            <p className={css({ textStyle: "body-1-subtle" })}>Body 1 Subtle</p>
            <p className={css({ textStyle: "body-2-intense" })}>Body 2 Intense</p>
            <p className={css({ textStyle: "body-2-subtle" })}>Body 2 Subtle</p>
            <p className={css({ textStyle: "body-3-intense" })}>Body 3 Intense</p>
            <p className={css({ textStyle: "body-3-subtle" })}>Body 3 Subtle</p>
            <p className={css({ textStyle: "body-3-caps" })}>Body 3 Caps</p>
            <p className={css({ textStyle: "body-4-subtle" })}>Body 4 Subtle</p>
            <p className={css({ textStyle: "body-4-uppercase" })}>Body 4 Uppercase</p>
          </div>

          <div className={textGroupStyles}>
            <div className={truncatedContainerStyles}>
              <p
                className={css(
                  { textStyle: "body-2-subtle" },
                  textTruncateStyles.raw({ truncated: "1-line" })
                )}
              >
                This is a very long text that will be truncated to one line. It contains many words
                and should demonstrate the truncation behavior.
              </p>
            </div>
            <div className={truncatedContainerStyles}>
              <p
                className={css(
                  { textStyle: "body-2-subtle" },
                  textTruncateStyles.raw({ truncated: "2-lines" })
                )}
              >
                This is a very long text that will be truncated to two lines. It contains many words
                and should demonstrate the truncation behavior. This is a very long text that will
                be truncated to two lines. It contains many words and should demonstrate the
                truncation behavior.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={sectionStyles}>
        <h2 className={titleStyles}>Input</h2>
        <div className={rowStyles}>
          <p className={labelStyles}>default</p>
          <input type="text" placeholder="Input text..." className={textInputStyles} />
          <textarea placeholder="Input text..." className={textInputStyles} />
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>disabled</p>
          <input type="text" placeholder="Input text..." className={textInputStyles} disabled />
          <textarea placeholder="Input text..." className={textInputStyles} disabled />
        </div>
      </div>
      <LoadingStates />
      <ScrollableAreaDemo />
    </div>
  );
}

const LoadingStates = () => {
  const [progress, setProgress] = useState(0);
  const [slowProgress, setSlowProgress] = useState(0);

  useEffect(() => {
    const increment = () => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 10));
    };
    let isMounted = true;
    const slowlyIncrementRecursively = async () => {
      if (!isMounted) {
        return;
      }
      setSlowProgress((prev) => (prev + 1 > 100 ? 0 : prev + 1));
      await sleep(Math.random() * 1000);
      slowlyIncrementRecursively();
    };

    slowlyIncrementRecursively();
    const fasterIntervalId = setInterval(increment, 1000);

    return () => {
      isMounted = false;
      clearInterval(fasterIntervalId);
    };
  }, []);

  return (
    <div className={sectionStyles}>
      <h2 className={titleStyles}>Loading States</h2>
      <div className={subSectionStyles}>
        <h3 className={subtitleStyles}>Spinner Component</h3>
        <div className={rowStyles}>
          <p className={labelStyles}>size 16</p>
          <Spinner size={16} />
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>size 24</p>
          <Spinner size={24} />
        </div>
        <div className={rowStyles}>
          <p className={labelStyles}>size 32</p>
          <Spinner size={32} />
        </div>
      </div>

      <div className={subSectionStyles}>
        <h3 className={subtitleStyles}>Circular Progress</h3>
        <div className={rowStyles}>
          <CircularProgress size="sm" progress={progress} />
          <CircularProgress size="md" progress={slowProgress} />
          <CircularProgress size="lg" progress={slowProgress} />
        </div>
        <div className={rowStyles}>
          <CircularProgress size="sm" progress={slowProgress} color="purple" />
          <CircularProgress size="md" progress={progress} color="purple" />
          <CircularProgress size="lg" progress={progress} thickness={10} color="purple" />
        </div>
      </div>
    </div>
  );
};

const ScrollableAreaDemo = () => {
  const [showScrollbars, setShowScrollbars] = useState(false);

  return (
    <div className={sectionStyles}>
      <h2 className={titleStyles}>ScrollArea</h2>
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
        })}
      >
        <span className={css({ textStyle: "body-3-subtle" })}>Scrollbar Variant:</span>
        <span className={css({ textStyle: "body-3-subtle" })}>Default</span>
        <Switch checked={showScrollbars} onCheckedChange={setShowScrollbars} />
        <span className={css({ textStyle: "body-3-subtle" })}>Visible</span>
      </div>
      <div className={subSectionStyles}>
        <h3 className={subtitleStyles}>Vertical</h3>
        <div className={rowStyles}>
          <ScrollArea
            scrollbarVariant={showScrollbars ? "visible" : "default"}
            className={css({
              width: "200px",
              height: "100px",
              border: "1px solid $gray.800",
            })}
          >
            <div className={css({ padding: "12px" })}>
              {Array.from({ length: 10 }, (_, i) => (
                <p
                  key={i}
                  className={css({
                    marginBottom: "8px",
                    border: "solid 1px token(colors.$gray.600)",
                    padding: "0 10px",
                    borderRadius: "1px",
                  })}
                >
                  Item {i + 1}
                </p>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
      <div className={subSectionStyles}>
        <h3 className={subtitleStyles}>Horizontal</h3>
        <div className={rowStyles}>
          <ScrollArea
            scrollbarVariant={showScrollbars ? "visible" : "default"}
            className={css({
              width: "300px",
              border: "1px solid $gray.800",
            })}
          >
            <div
              className={css({
                padding: "12px",
                display: "flex",
                gap: "8px",
                width: "max-content",
              })}
            >
              {Array.from({ length: 10 }, (_, i) => (
                <div
                  key={i}
                  className={css({
                    border: "solid 1px token(colors.$gray.600)",
                    padding: "8px 12px",
                    whiteSpace: "nowrap",
                  })}
                >
                  Item {i + 1}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

const rootStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  padding: "16px",
});

const sectionStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "12px 16px 20px",

  // slightly different background to match that of the design system figma for better contrast
  background: "#181818",
  borderRadius: "8px",
  border: "1px solid token(colors.$gray.800)",
});

const titleStyles = css({
  fontSize: "30px",
  textShadow: "2px 2px 0 token(colors.$purple)",
});

const subSectionStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
});

const subtitleStyles = css({
  fontSize: "20px",
});

const rowStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "10px",
});

const labelStyles = css({
  padding: "1px 6px",
  border: "1px dashed token(colors.$purple)",
  fontFamily: "monospace",
  borderRadius: "4px",
});

const textSectionStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

const textGroupStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const truncatedContainerStyles = css({
  width: "300px",
  border: "1px solid token(colors.$gray.800)",
  padding: "8px",
  borderRadius: "4px",
});
